/**
 * Sentry wrapper to handle ESM/CommonJS import issues
 * This provides a safe interface to Sentry that works in both dev and prod
 */

import { isDocumentTelemetryEligible, whenDocumentTelemetryDecided } from '@/lib/privacy/eligibility';
import { scrubTelemetryValue } from '@/lib/privacy/scrub';

const ENTRY_TEXT_LIMIT = 200;
const MAX_SPANS = 500;
const SOFT_BYTE_LIMIT = 250_000;
const HARD_BYTE_LIMIT = 262_144;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

function utf8ByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}

function replaceInlinePayload(value: string): string {
  if (value.startsWith('data:')) {
    const comma = value.indexOf(',');
    const meta = comma === -1 ? value.slice(5) : value.slice(5, comma);
    const semi = meta.indexOf(';');
    const mediaType = (semi === -1 ? meta : meta.slice(0, semi)) || 'text/plain';
    return `data:${mediaType} (${value.length} bytes)`;
  }
  if (value.startsWith('blob:')) {
    return `blob:(${value.length} bytes)`;
  }
  return value;
}

function limitEntryText(value: string): string {
  const replaced = replaceInlinePayload(value);
  return replaced.length > ENTRY_TEXT_LIMIT ? replaced.slice(0, ENTRY_TEXT_LIMIT) : replaced;
}

const TRIM_DEPTH_LIMIT = 8;

function trimEntryData(value: unknown, depth = 0, seen?: WeakSet<object>): unknown {
  if (typeof value === 'string') return limitEntryText(value);
  if (value == null || typeof value !== 'object') return value;
  if (depth > TRIM_DEPTH_LIMIT) return undefined;
  const visiting = seen ?? new WeakSet<object>();
  if (visiting.has(value)) return undefined;
  if (Array.isArray(value)) {
    visiting.add(value);
    const next = value.map((item) => trimEntryData(item, depth + 1, visiting));
    visiting.delete(value);
    return next;
  }
  if (!isPlainObject(value)) return value;
  visiting.add(value);
  const next: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    next[key] = trimEntryData(child, depth + 1, visiting);
  }
  visiting.delete(value);
  return next;
}

function spanToPlain(span: unknown): unknown {
  if (!span || typeof span !== 'object') return span;
  const host = span as { toJSON?: () => unknown };
  if (typeof host.toJSON !== 'function') return span;
  try {
    const rendered = host.toJSON();
    return rendered === span ? span : rendered;
  } catch {
    return span;
  }
}

function trimSpan(span: unknown): unknown {
  const plain = spanToPlain(span);
  if (!isPlainObject(plain)) return plain;
  const next: Record<string, unknown> = { ...plain };
  if (typeof next.description === 'string') next.description = limitEntryText(next.description);
  if ('data' in next) next.data = trimEntryData(next.data);
  return next;
}

function mentionsNetlify(value: unknown, depth = 0, seen?: WeakSet<object>): boolean {
  if (typeof value === 'string') return value.includes('/.netlify/');
  if (value == null || typeof value !== 'object' || depth > TRIM_DEPTH_LIMIT) return false;
  const visiting = seen ?? new WeakSet<object>();
  if (visiting.has(value)) return false;
  visiting.add(value);
  const children = Array.isArray(value) ? value : isPlainObject(value) ? Object.values(value) : [];
  const found = children.some((item) => mentionsNetlify(item, depth + 1, visiting));
  visiting.delete(value);
  return found;
}

function trimBreadcrumb(crumb: unknown): unknown {
  if (!isPlainObject(crumb)) return crumb;
  const next: Record<string, unknown> = { ...crumb };
  if (typeof next.message === 'string') next.message = limitEntryText(next.message);
  if ('data' in next) next.data = trimEntryData(next.data);
  return next;
}

function trimBreadcrumbs(breadcrumbs: unknown): unknown {
  if (Array.isArray(breadcrumbs)) return breadcrumbs.map((crumb) => trimBreadcrumb(crumb));
  if (!isPlainObject(breadcrumbs) || !Array.isArray(breadcrumbs.values)) return breadcrumbs;
  return {
    ...breadcrumbs,
    values: breadcrumbs.values.map((crumb) => trimBreadcrumb(crumb)),
  };
}

function keepUserIdOnly(event: unknown): unknown {
  if (!isPlainObject(event) || !('user' in event) || event.user == null) return event;
  const user = event.user;
  if (!isPlainObject(user)) {
    delete event.user;
    return event;
  }
  const id = user.id;
  if (typeof id === 'string' || typeof id === 'number') {
    event.user = { id };
  } else {
    delete event.user;
  }
  return event;
}

function measuredBytes(event: Record<string, unknown>): number | null {
  try {
    const { sdkProcessingMetadata: _metadata, ...rest } = event;
    return utf8ByteLength(JSON.stringify(rest));
  } catch {
    return null;
  }
}

function spanWeight(span: unknown): number {
  try {
    return utf8ByteLength(JSON.stringify(span));
  } catch {
    return 0;
  }
}

function isResourceSpan(span: unknown): boolean {
  return isPlainObject(span) && typeof span.op === 'string' && span.op.startsWith('resource.');
}

function dropResourceSpans(event: Record<string, unknown>): void {
  if (!Array.isArray(event.spans)) return;
  const initial = measuredBytes(event);
  if (initial == null || initial <= SOFT_BYTE_LIMIT) return;
  const spans = event.spans;
  const ranked: unknown[] = [];
  const weights: number[] = [];
  for (const span of spans) {
    if (!isResourceSpan(span)) continue;
    const weight = spanWeight(span);
    let insertAt = ranked.length;
    for (let index = 0; index < weights.length; index += 1) {
      if (weight > weights[index]) {
        insertAt = index;
        break;
      }
    }
    ranked.splice(insertAt, 0, span);
    weights.splice(insertAt, 0, weight);
  }
  if (ranked.length === 0) return;

  const sizeAfterDropping = (dropCount: number): number | null => {
    const dropping = new Set(ranked.slice(0, dropCount));
    const saved = event.spans;
    event.spans = spans.filter((span) => !dropping.has(span));
    const size = measuredBytes(event);
    event.spans = saved;
    return size;
  };

  let low = 1;
  let high = ranked.length;
  let needed = ranked.length;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    const size = sizeAfterDropping(mid);
    if (size != null && size <= SOFT_BYTE_LIMIT) {
      needed = mid;
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }
  const dropping = new Set(ranked.slice(0, needed));
  event.spans = spans.filter((span) => !dropping.has(span));
}

function applyHardFallback(event: Record<string, unknown>): boolean {
  const size = measuredBytes(event);
  if (size == null) return false;
  if (size <= SOFT_BYTE_LIMIT) return true;
  event.spans = [];
  const tags = isPlainObject(event.tags) ? { ...event.tags } : {};
  tags['rdv.trim'] = 'hard';
  event.tags = tags;
  const after = measuredBytes(event);
  return after != null && after <= HARD_BYTE_LIMIT;
}

/**
 * REN-362 / REN-348 regression. Sentry 7 passes live Span objects into
 * beforeSendTransaction. Convert them with toJSON before any walk, trim the
 * plain entries, scrub, then measure the bytes that will actually be sent.
 * sdkProcessingMetadata stays out of the span walk. Sentry 7 reads
 * dynamicSamplingContext for the envelope trace header and
 * spanCountBeforeProcessing for dropped-span accounting, then deletes the
 * object before the item is serialized. Only those two keys are reattached,
 * and the sampling context is scrubbed.
 */
function prepareSentryTransaction(event: unknown): unknown {
  if (!isPlainObject(event)) return keepUserIdOnly(scrubTelemetryValue(event));

  const metadata = event.sdkProcessingMetadata;
  const copy: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(event)) {
    if (key === 'sdkProcessingMetadata') continue;
    copy[key] = value;
  }
  if (Array.isArray(event.spans)) {
    const trimmed = event.spans
      .map((span) => trimSpan(span))
      .filter((span) => !mentionsNetlify(span));
    copy.spans = trimmed.slice(0, MAX_SPANS);
  }
  if ('breadcrumbs' in event) copy.breadcrumbs = trimBreadcrumbs(event.breadcrumbs);

  const scrubbed = scrubTelemetryValue(copy);
  if (!isPlainObject(scrubbed)) return null;
  keepUserIdOnly(scrubbed);
  dropResourceSpans(scrubbed);
  if (!applyHardFallback(scrubbed)) return null;

  if (isPlainObject(metadata)) {
    const kept: Record<string, unknown> = {
      dynamicSamplingContext: scrubTelemetryValue(metadata.dynamicSamplingContext),
    };
    if (typeof metadata.spanCountBeforeProcessing === 'number') {
      kept.spanCountBeforeProcessing = metadata.spanCountBeforeProcessing;
    }
    scrubbed.sdkProcessingMetadata = kept;
  }
  return scrubbed;
}

interface MockSentry {
  init: (options: any) => void;
  withProfiler: <T>(component: T, name?: string) => T;
  ErrorBoundary: React.ComponentType<any>;
  captureException: (error: any, context?: any) => void;
  captureMessage: (message: string) => void;
  browserTracingIntegration: () => any;
  replayIntegration: (options?: any) => any;
  startSpan: (options: any, callback: () => any) => any;
  setTag: (key: string, value: string) => void;
  addBreadcrumb: (breadcrumb: any) => void;
  withScope: (callback: (scope: any) => void) => void;
}

const mockSentry: MockSentry = {
  init: () => console.log('Sentry disabled in development'),
  withProfiler: (component) => component,
  ErrorBoundary: ({ children }: any) => children,
  captureException: (error, context) => console.error('Sentry (dev):', error, context),
  captureMessage: (message) => console.log('Sentry (dev):', message),
  browserTracingIntegration: () => ({}),
  replayIntegration: () => ({}),
  startSpan: (options, callback) => callback(),
  setTag: () => {},
  addBreadcrumb: (breadcrumb) => console.log('Sentry breadcrumb:', breadcrumb),
  withScope: (callback) => callback({ 
    setTag: () => {}, 
    setContext: () => {},
    setFingerprint: () => {}
  }),
};

interface SentryInitModule {
  browserTracingIntegration: () => unknown;
}

/** Replay is not installed. Session capture stays off until that channel is proven token-free. */
export function buildSentryInitOptions(module: SentryInitModule) {
  return {
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [module.browserTracingIntegration()],
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    autoSessionTracking: false,
    sendClientReports: false,
    beforeBreadcrumb(breadcrumb: unknown) {
      return scrubTelemetryValue(breadcrumb);
    },
    beforeSend(event: unknown, hint: { originalException?: unknown }) {
      const error = hint?.originalException;
      if (error && String(error).includes('JWT expired')) return null;
      return keepUserIdOnly(scrubTelemetryValue(event));
    },
    beforeSendTransaction(event: unknown) {
      return prepareSentryTransaction(event);
    },
  };
}

// Only import Sentry in production, and only after the boot guard says this document is eligible.
let SentryModule: any = mockSentry;

if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
  void whenDocumentTelemetryDecided().then(() => {
    if (!isDocumentTelemetryEligible()) return;
    import('@sentry/react').then((module) => {
      if (!isDocumentTelemetryEligible()) return;
      SentryModule = module;
      SentryModule.init(buildSentryInitOptions(module));
    }).catch((error) => {
      console.error('Failed to load Sentry:', error);
    });
  });
}

export default SentryModule;

export function withProfiler<T>(component: T, name?: string): T {
  try { return SentryModule.withProfiler ? SentryModule.withProfiler(component, name) : component; } catch { return component; }
}

export function captureException(error: any, context?: any): void {
  try { SentryModule.captureException?.(error, context); } catch { /* noop */ }
}

export function captureMessage(message: string): void {
  try { SentryModule.captureMessage?.(message); } catch { /* noop */ }
}

export function startSpan<T = any>(options: any, callback: () => T): T {
  try { return SentryModule.startSpan ? SentryModule.startSpan(options, callback) : callback(); } catch { return callback(); }
}

export function setTag(key: string, value: string): void {
  try { SentryModule.setTag?.(key, value); } catch { /* noop */ }
}

export function addBreadcrumb(breadcrumb: any): void {
  try { SentryModule.addBreadcrumb?.(breadcrumb); } catch { /* noop */ }
}

export function withScope(callback: (scope: any) => void): void {
  try { SentryModule.withScope ? SentryModule.withScope(callback) : callback({ setTag: () => {}, setContext: () => {}, setFingerprint: () => {} }); } catch { /* noop */ }
}